import { useEffect } from "react";
import {
	JobWithShare,
	SupabaseShareEntityViewEnum,
	SupabaseTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { FileInGallery } from "@/dentlab/src/components/chat/messages/job-request-custom/components/file-in-message/file-in-gallery";
import { FileViewerModal } from "@components/file-viewer";
import { useFileHandler } from "@/lib/hooks/FileHandler/useFileHandler";
import { ChevronRight, ExternalLink } from "lucide-react";
import { useJobSheet } from "../../../sheet-hooks/useJobSheet";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { FileEntityTypeWithUrl } from "@/dentlab/src/hooks/useStorage";
import { cn } from "@/dentlab/src/lib/util";
import { JobDocumentTags } from "@/dentlab/src/pages/job-page/job-document/job-document-tag.component";
import { JobStatusEnum, jobStatusToLabel } from "@/lib/types/job";

export const PatientJobs: React.FC<{
	patientId: string;
	jobId?: number;
	openIcon?: React.ReactNode;
	size?: "sm" | "md";
}> = ({ patientId, jobId, size = "md" }) => {
	const [jobs, setJobs] = useState<JobWithShare[]>([]);
	const [loadingJobs, setLoadingJobs] = useState<boolean>(true);
	const [detailsLoaded, setDetailsLoaded] = useState(false);
	const [fileIds, setFileIds] = useState<string[]>([]);
	const { clientsLookup, practitionersLookup } = useCentralStore((state) => ({
		clientsLookup: state.clientsLookup,
		practitionersLookup: state.practitionersLookup,
	}));

	const { shareFile, unshareFile, acceptJobRequest, denyJobRequest } =
		useJobStore((state) => ({
			shareFile: state.shareFile,
			unshareFile: state.unshareFile,
			acceptJobRequest: state.acceptJobRequest,
			denyJobRequest: state.denyJobRequest,
		}));

	const { openJobSheet } = useJobSheet();

	const { files, loading: loadingFiles } = useFileHandler(fileIds);

	const getPatientJobs = async () => {
		setLoadingJobs(true);
		const { error, data } = await supabase
			.from(SupabaseShareEntityViewEnum.JOBS_WITH_SHARES)
			.select()
			.eq("patient_id", patientId);
		if (error) {
			Logger.error(error, {}, "Error fetching jobs for patient");
			return;
		}
		setJobs(
			data
				.filter((job) => {
					return jobId ? job.id !== jobId : true;
				})
				.sort(
					(a, b) =>
						new Date(b.created_at ?? "").getTime() -
						new Date(a.created_at ?? "").getTime()
				)
		);
		setLoadingJobs(false);
	};

	const getPatientFiles = async () => {
		const jobIds = jobs.map((job) => job.id).filter(Boolean) as number[];
		if (jobIds.length === 0) {
			setDetailsLoaded(true);
			return;
		}

		const { data, error } = await supabase
			.from(SupabaseTableEnum.FILES)
			.select()
			.in("job_id", jobIds)
			.is("job_document_id", null);

		if (error) {
			Logger.error(error, {}, "Error fetching files for patient");
		} else {
			const filtered = data.filter(
				(file) => !file.file_name?.includes("etikett")
			);
			setFileIds(filtered.map((file) => file.id));
		}

		setDetailsLoaded(true);
	};

	const handleOpenJobDetail = (job: JobWithShare) => {
		openJobSheet({
			job: job,
			onShareFile: shareFile,
			onUnshareFile: unshareFile,
			onAcceptJobRequest: acceptJobRequest,
			onDenyJobRequest: denyJobRequest,
		});
	};

	useEffect(() => {
		getPatientJobs();
	}, [patientId, jobId]);

	useEffect(() => {
		getPatientFiles();
	}, [jobs]);

	if (loadingJobs || !detailsLoaded) {
		return (
			<div className="text-center py-3 text-xs text-gray-400">
				Lade Aufträge...
			</div>
		);
	}

	if (jobs.length === 0 && !jobId) {
		return (
			<div className="text-center py-3 text-xs text-gray-400">
				Keine Aufträge
			</div>
		);
	}

	const getStatusLabel = (job: JobWithShare): string => {
		const s = job.status as keyof typeof jobStatusToLabel;
		if (s === JobStatusEnum.COMPLETED) return "Abgeschlossen";
		return jobStatusToLabel[s] ?? "–";
	};

	const grouped = jobs.reduce<Record<string, JobWithShare[]>>((acc, job) => {
		const status = getStatusLabel(job);
		(acc[status] ??= []).push(job);
		return acc;
	}, {});

	return (
		<div>
			{Object.entries(grouped).map(([status, groupJobs]) => (
				<div key={status}>
					<div className="px-2 py-1.5 text-xs font-bold text-black border-b border-gray-200 bg-gray-50">
						{status}
					</div>
					<div className="divide-y divide-gray-100">
						{groupJobs.map((job, index) => {
							const clientName =
								(clientsLookup[job.client_id ?? ""]
									?.first_name ?? "") +
								" " +
								(clientsLookup[job.client_id ?? ""]
									?.last_name ?? "");
							const prac = job.practitioner_id
								? practitionersLookup[job.practitioner_id]
								: null;
							const practitionerName = prac
								? `${prac.first_name ?? ""} ${
										prac.last_name ?? ""
									}`.trim()
								: "";
							return (
								<JobRow
									key={job.id}
									job={job}
									index={index}
									handleOpenJobDetail={handleOpenJobDetail}
									clientName={clientName}
									practitionerName={practitionerName}
									files={files.filter(
										(file) => file.job_id === job.id
									)}
									size={size}
									statusLabel={status}
								/>
							);
						})}
					</div>
				</div>
			))}
		</div>
	);
};

const formatDateTime = (
	dateStr: string | null | undefined,
	timeStr: string | null | undefined
) => {
	if (!dateStr) return "–";
	const date = new Date(dateStr).toLocaleDateString("de-CH", {
		day: "2-digit",
		month: "2-digit",
		year: "2-digit",
	});
	if (!timeStr) return date;
	return `${date} ${timeStr.slice(0, 5)}`;
};

const JobRow: React.FC<{
	job: JobWithShare;
	index: number;
	handleOpenJobDetail: (job: JobWithShare) => void;
	clientName: string;
	practitionerName: string;
	files: FileEntityTypeWithUrl[];
	size?: "sm" | "md";
	statusLabel: string;
}> = ({
	job,
	index,
	handleOpenJobDetail,
	clientName,
	practitionerName,
	files,
	size = "md",
	statusLabel,
}) => {
	const [expanded, setExpanded] = useState(false);
	const [isViewerOpen, setIsViewerOpen] = useState(false);
	const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

	const handleFileClick = (fileId: string) => {
		setSelectedFileId(fileId);
		setIsViewerOpen(true);
	};

	const isSmall = size === "sm";

	return (
		<div>
			{/* Compact header rows */}
			<div
				className={cn(
					"flex cursor-pointer hover:bg-gray-50 transition-colors",
					isSmall ? "py-1 px-1 gap-1" : "py-1.5 px-2 gap-1.5"
				)}
				onClick={() => setExpanded(!expanded)}
			>
				<ChevronRight
					className={cn(
						"shrink-0 text-gray-400 transition-transform duration-150 mt-0.5",
						isSmall ? "h-3 w-3" : "h-3.5 w-3.5",
						expanded && "rotate-90"
					)}
				/>
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-1.5">
						<span
							className={cn(
								"font-medium shrink-0",
								isSmall ? "text-xs" : "text-sm"
							)}
						>
							{job?.code ?? ""}
						</span>
						<div className="flex-1" />
						<JobDocumentTags job={job} />
						<button
							className={cn(
								"shrink-0 text-gray-400 hover:text-gray-700 transition-colors",
								isSmall ? "p-0.5" : "p-1"
							)}
							onClick={(e) => {
								e.stopPropagation();
								handleOpenJobDetail(job);
							}}
							aria-label="Auftrag öffnen"
						>
							<ExternalLink
								className={isSmall ? "h-3 w-3" : "h-3.5 w-3.5"}
							/>
						</button>
					</div>
					<div className="flex items-center gap-1.5">
						<span
							className={cn(
								"text-gray-500 truncate flex-1",
								isSmall ? "text-xs" : "text-sm"
							)}
						>
							{job?.title ?? ""}
						</span>
						<span
							className={cn(
								"text-gray-400 shrink-0 tabular-nums",
								isSmall ? "text-[10px]" : "text-xs"
							)}
						>
							{formatDateTime(
								job.incoming_order_date,
								job.incoming_order_time
							)}
						</span>
					</div>
				</div>
			</div>

			{/* Expanded details */}
			{expanded && (
				<div
					className={cn(
						"bg-gray-50 border-t border-gray-100",
						isSmall ? "px-2 py-1.5 pl-5" : "px-3 py-2 pl-7"
					)}
				>
					<div
						className={cn(
							"grid gap-x-3 gap-y-0.5",
							isSmall ? "text-xs" : "text-sm"
						)}
						style={{ gridTemplateColumns: "auto minmax(0, 1fr)" }}
					>
						<span className="text-gray-400">Kunde</span>
						<span className="break-words">{clientName}</span>
						{job.description && (
							<>
								<span className="text-gray-400">
									Beschreibung
								</span>
								<span className="break-words">
									{job.description}
								</span>
							</>
						)}
						{job.tooth_color && (
							<>
								<span className="text-gray-400">Zahnfarbe</span>
								<span>{job.tooth_color}</span>
							</>
						)}
						{practitionerName && (
							<>
								<span className="text-gray-400">Behandler</span>
								<span className="break-words">
									{practitionerName}
								</span>
							</>
						)}
						<span className="text-gray-400">Eingang</span>
						<span className="tabular-nums">
							{formatDateTime(
								job.incoming_order_date,
								job.incoming_order_time
							)}
						</span>
						<span className="text-gray-400">Ausgang</span>
						<span className="tabular-nums">
							{formatDateTime(
								job.outgoing_order_date,
								job.outgoing_order_time
							)}
						</span>
						{job.kv_total_final != null &&
							job.kv_total_final > 0 && (
								<>
									<span className="text-gray-400">Total</span>
									<span className="font-medium tabular-nums">
										{job.kv_total_final.toFixed(2)}
									</span>
								</>
							)}
					</div>

					{files.length > 0 && (
						<div
							className={cn(
								"flex flex-row gap-1 flex-wrap",
								isSmall ? "mt-1.5" : "mt-2"
							)}
						>
							{files.map((file) => (
								<FileInGallery
									key={file.id}
									file={file}
									size="small"
									onFileClick={handleFileClick}
								/>
							))}
						</div>
					)}
				</div>
			)}

			<FileViewerModal
				fileIds={files.map((f) => f.id)}
				defaultFileId={selectedFileId || undefined}
				isOpen={isViewerOpen}
				onClose={() => {
					setIsViewerOpen(false);
					setSelectedFileId(null);
				}}
			/>
		</div>
	);
};
